// U3 "Start easily" (docs/IMPROVEMENT-PLAN.md): what a card asks, roughly how long it takes, and
// the one highlighted next step. Pure and zod-free, so the web bundle and the server share it.
// The judge suggests an effort; the runtime adjusts it here from what the card already holds
// (a ready draft makes a reply quick; research or scheduling still to do makes it bigger).

export const EFFORTS = ["quick", "short", "long"] as const;
export type Effort = (typeof EFFORTS)[number];

/** Minutes behind each effort; "long" cards are counted apart ("needs focus time"). */
export const EFFORT_MINUTES: Record<Effort, number> = { quick: 1, short: 5, long: 25 };

export function isEffort(value: unknown): value is Effort {
  return typeof value === "string" && (EFFORTS as readonly string[]).includes(value);
}

export interface CardNeeds {
  draft: boolean;
  research: boolean;
  scheduling: boolean;
  decision: boolean;
}

export interface CardDeadline {
  /** As the judge wrote it: an ISO instant or a `YYYY-MM-DD` day. */
  at: string;
  /** The email's own words for it ("by Wed EOD"). */
  quote: string;
}

/** The ask, in one line: who wants what (null when nothing is asked), by when, and what it needs. */
export interface CardAsk {
  text: string | null;
  deadline: CardDeadline | null;
  needs: CardNeeds | null;
}

/** True when the card has something to say on its ask line. */
export function hasAsk(ask: CardAsk | null | undefined): boolean {
  return !!ask && (!!ask.text?.trim() || !!ask.deadline);
}

interface BlockLike {
  id: string;
  type: string;
  value?: string | undefined;
  text?: string | undefined;
}

interface ActionLike {
  behavior: string;
  actionKind?: string | undefined;
  artifactBlockId?: string | undefined;
}

export interface FaceLike {
  blocks: readonly BlockLike[];
  actions: readonly ActionLike[];
}

const SENDS = new Set(["gmail.send_reply", "gmail.forward"]);

/** A send whose draft is already written: the reply is a read-and-press. */
export function hasReadyDraft(face: FaceLike): boolean {
  return face.actions.some((a) => {
    if (a.behavior !== "approve_action" || !a.actionKind || !SENDS.has(a.actionKind)) return false;
    const draft = face.blocks.find((b) => b.id === a.artifactBlockId);
    return draft?.type === "editable_text" && !!(draft.value ?? draft.text ?? "").trim();
  });
}

const LEVEL: Record<Effort, number> = { quick: 0, short: 1, long: 2 };
const bump = (e: Effort, by: number): Effort =>
  EFFORTS[Math.max(0, Math.min(2, LEVEL[e] + by))] ?? e;

/** What a card without a judged effort (made before U3) is assumed to take. */
function fallbackEffort(needs: CardNeeds | null | undefined): Effort {
  if (!needs) return "short";
  return needs.draft || needs.decision || needs.scheduling || needs.research ? "short" : "quick";
}

/**
 * The effort the owner sees, decided by the runtime from the judge's suggestion and the card as it
 * stands: a ready draft brings a reply down one step to quick (long work stays at least short);
 * research or scheduling the card still lacks moves it up one step. Cards from before U3 (no
 * effort, maybe no needs) fall back to a safe middle.
 */
export function effectiveEffort(
  judged: Effort | null | undefined,
  needs: CardNeeds | null | undefined,
  face: FaceLike,
): Effort {
  let effort = isEffort(judged) ? judged : fallbackEffort(needs);
  if (hasReadyDraft(face)) effort = effort === "long" ? "short" : "quick";
  const has = (type: string) => face.blocks.some((b) => b.type === type);
  const unmetResearch = !!needs?.research && !has("research");
  const unmetScheduling = !!needs?.scheduling && !has("slots") && !hasReadyDraft(face);
  if (unmetResearch || unmetScheduling) effort = bump(effort, 1);
  return effort;
}

/** "about 1 min", "about 5 min", "needs focus time". */
export function effortWords(effort: Effort): string {
  if (effort === "long") return "needs focus time";
  return `about ${EFFORT_MINUTES[effort]} min`;
}

/** How long a set of cards takes; cards that need focus time are counted apart. */
export interface RoundEstimate {
  /** Cards counted. */
  count: number;
  /** Minutes for the quick and short cards. */
  minutes: number;
  quickCount: number;
  quickMinutes: number;
  /** Cards that need focus time (not in `minutes`). */
  longCount: number;
}

export function roundEstimate(efforts: readonly Effort[]): RoundEstimate {
  let minutes = 0;
  let quickCount = 0;
  let longCount = 0;
  for (const e of efforts) {
    if (e === "long") longCount++;
    else minutes += EFFORT_MINUTES[e];
    if (e === "quick") quickCount++;
  }
  return {
    count: efforts.length,
    minutes,
    quickCount,
    quickMinutes: quickCount * EFFORT_MINUTES.quick,
    longCount,
  };
}

/**
 * Minutes rounded kindly: exact under a quarter hour, to five minutes under an hour, to half
 * hours after that ("about 12 minutes", "about 25 minutes", "about 1½ hours").
 */
export function minutesWords(minutes: number): string | null {
  if (!(minutes > 0)) return null;
  if (minutes <= 1) return "about a minute";
  if (minutes < 15) return `about ${Math.round(minutes)} minutes`;
  if (minutes < 58) return `about ${Math.round(minutes / 5) * 5} minutes`;
  const halves = Math.max(2, Math.round(minutes / 30));
  if (halves === 2) return "about an hour";
  const whole = Math.floor(halves / 2);
  return halves % 2 ? `about ${whole}½ hours` : `about ${whole} hours`;
}

/** "about 3 min" style for tight spaces (the quick-ones offer). */
export function shortMinutesWords(minutes: number): string | null {
  if (!(minutes > 0)) return null;
  if (minutes < 58)
    return `about ${minutes < 15 ? Math.round(minutes) : Math.round(minutes / 5) * 5} min`;
  return minutesWords(minutes);
}

/** "2 need focus time" / "1 needs focus time" (no count when there are none). */
export function focusCountWords(n: number): string | null {
  if (n <= 0) return null;
  return n === 1 ? "1 needs focus time" : `${n} need focus time`;
}

// ---------------------------------------------------------------------------------------------
// The one highlighted next step
// ---------------------------------------------------------------------------------------------

export interface RecommendableAction {
  variant?: "primary" | "secondary" | undefined;
  recommended?: boolean | undefined;
}

/**
 * Index of the recommended action among a card's configured actions: the first one the model
 * marked, else the first primary one; -1 when there is none.
 */
export function recommendedIndex(actions: readonly RecommendableAction[]): number {
  const marked = actions.findIndex((a) => a.recommended === true);
  return marked >= 0 ? marked : actions.findIndex((a) => a.variant === "primary");
}

/**
 * Exactly one recommended action at most: it carries `recommended: true` and the primary style;
 * other actions lose a stray `recommended` and a second primary style, so the card shows one
 * highlighted next step. Cards with no marked and no primary action are returned as they are.
 */
export function withRecommended<T extends RecommendableAction>(actions: readonly T[]): T[] {
  const index = recommendedIndex(actions);
  if (index < 0) return actions.map((a) => stripRecommended(a));
  return actions.map((a, i) => {
    if (i === index) return { ...a, recommended: true, variant: "primary" as const };
    const rest = stripRecommended(a);
    return rest.variant === "primary" ? { ...rest, variant: "secondary" as const } : rest;
  });
}

function stripRecommended<T extends RecommendableAction>(a: T): T {
  if (a.recommended === undefined) return a;
  const { recommended: _drop, ...rest } = a;
  return rest as T;
}

// ---------------------------------------------------------------------------------------------
// Rounds
// ---------------------------------------------------------------------------------------------

/** How a round's cards were ordered when it started; never changed mid-round. */
export const ROUND_ORDERS = ["standard", "quick_first"] as const;
export type RoundOrder = (typeof ROUND_ORDERS)[number];

/** The focus block made for a big card; the card waits under Lined up until it starts. */
export interface CardFocusBlock {
  approvalId: string;
  workId: string;
  start: string;
  end: string;
  tz: string;
}

/**
 * The link a lined-up card shows for its block: the event itself once Google returned its link,
 * else that day in Google Calendar (in the block's zone).
 */
export function focusBlockLink(block: CardFocusBlock, eventLink: string | null): string {
  if (eventLink?.startsWith("https://")) return eventLink;
  const day = new Intl.DateTimeFormat("en-CA", {
    timeZone: block.tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(new Date(block.start))
    .replaceAll("-", "/");
  return `https://calendar.google.com/calendar/r/day/${day}`;
}

/** Where a focus session stopped in a round, so it can pick up there. */
export interface FocusPosition {
  passNo: number;
  /** 0-based step the owner was on. */
  index: number;
  /** Steps in the session (cards, then the round's suggested cleanups). */
  total: number;
  /** The card (or cleanup step) at `index`, so a changed round still finds it. */
  stepId: string | null;
  at: string;
}
